import { useState } from 'react';

import useTranslation from '@/utils/hooks/translation/useTranslation';
// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Utils
import { deleteTag, detachTagFromMedia } from '@/utils/media/library';

import Dropdown, { BaseItem } from '@/components/base/Dropdown';
// Components
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
import ActionTag from '@/components/base/Tag/ActionTag';

const TagsList = ({
    tags = [],
    mutateLibraryMedia,
    selectedMediaIds = [],
    removeTagsCallback = () => null
}) => {
    const { t } = useTranslation('media');
    const { activeSocialProfile } = useActiveSocialProfile();
    const [{ isLoading, hasError, tagId }, setApiState] = useState({
        isLoading: false,
        hasError: null,
        tagId: null
    });

    const handleDetachTagFromMedias = async (tag) => {
        setApiState({ isLoading: true, hasError: false, tagId: tag?.id });
        try {
            await detachTagFromMedia(
                tag,
                activeSocialProfile.id,
                selectedMediaIds,
                mutateLibraryMedia
            );
            removeTagsCallback(tag);
            setApiState({ isLoading: false, hasError: false, tagId: null });
        } catch (error) {
            setApiState({ isLoading: false, hasError: true, tagId: null });
        }
    };

    const handleDeleteTag = async (tag) => {
        setApiState({ isLoading: true, hasError: false, tagId: tag?.id });
        try {
            await deleteTag(tag, activeSocialProfile.id, mutateLibraryMedia);
            removeTagsCallback(tag);
            setApiState({ isLoading: false, hasError: false, tagId: null });
        } catch (error) {
            setApiState({ isLoading: false, hasError: true, tagId: null });
        }
    };

    return (
        <>
            {hasError && <p className="mb-2 text-xs text-icoRed">{t('errors:error_occured')}</p>}
            <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                    <div key={tag.id}>
                        <Dropdown
                            button={
                                <Dropdown.Button
                                    disabled={isLoading}
                                    className="rounded-full focus:ring-offset-0"
                                    name={tag.label}>
                                    <ActionTag
                                        icon="label"
                                        variant={isLoading ? 'disabled' : 'default'}>
                                        <span className="mr-1 capitalize">{tag.tag}</span>
                                        {isLoading && tagId === tag?.id ? (
                                            <Loader className="h-3 w-3" />
                                        ) : (
                                            <Icon icon="chevron-bottom" className="text-xxs" />
                                        )}
                                    </ActionTag>
                                </Dropdown.Button>
                            }
                            position="center">
                            <Dropdown.Divider>
                                <Dropdown.Item
                                    data-testid={`delete-from-media-${tag.tag}`}
                                    onClick={() => handleDetachTagFromMedias(tag)}>
                                    {({ active }) => (
                                        <BaseItem active={active}>
                                            <span className="text-icoGray-500 dark:text-icoDarkMode-wolf">
                                                {t('remove_tag_from_media', {
                                                    count: selectedMediaIds.length
                                                })}
                                            </span>
                                        </BaseItem>
                                    )}
                                </Dropdown.Item>
                                <Dropdown.Item
                                    data-testid={`delete-${tag.tag}`}
                                    onClick={() => handleDeleteTag(tag)}>
                                    {({ active }) => (
                                        <BaseItem active={active}>
                                            <span className="text-icoGray-500 dark:text-icoDarkMode-wolf">
                                                {t('delete_tag')}
                                            </span>
                                        </BaseItem>
                                    )}
                                </Dropdown.Item>
                            </Dropdown.Divider>
                        </Dropdown>
                    </div>
                ))}
            </div>
        </>
    );
};

export default TagsList;
